import type { FlowTarget, GraphData, GraphNode, ValueInput } from '../../scenedata/behaviors/behaviorgraphdata';

// Self-contained behavior-graph helper (types only) so it's safe to import from
// the editor/action layer without dragging the store graph in. Mirrors the node
// access in behaviorGraphMigration.ts.
//
// Staged into trace-viewer/editor from TraceFrontend (utils/stateReferenceRewrite.ts)
// as part of the state-group centralization (Phase 1). Pure: in-place graph edit
// over GraphData, no SceneObject / undo coupling.

function nodeOp(data: GraphData, node: GraphNode): string {
  return data.declarations[node.declaration]?.op ?? '';
}

/**
 * Rewrite the inline `state` name of every `trace/setState` node from `from`
 * to `to`, IN PLACE. Returns true if anything changed.
 *
 * Used for:
 * - rename propagation — `to` is the new state name, so interactions keep firing
 *   after a state is renamed (state references are by name; the runtime
 *   `setState(unknownName)` is a silent no-op);
 * - delete redirect — `to` is `''`, which the setState block interprets as
 *   "revert to base" (`setState(undefined)`), so a button whose target state was
 *   deleted returns the object to its base instead of silently doing nothing.
 *   Runtime contract: trace-viewer TraceSetStateBlock maps `state === '' →
 *   undefined`; the inline-write side is covered by stateReferenceRewrite.test.ts.
 *
 * Node-ref (dynamic) `state` inputs and non-matching names are left untouched.
 */
export function rewriteSetStateName(
  graph: GraphData | null | undefined,
  from: string,
  to: string,
): boolean {
  if (!graph || !Array.isArray(graph.nodes)) return false;

  let changed = false;
  for (const node of graph.nodes) {
    if (nodeOp(graph, node) !== 'trace/setState') continue;

    const vi = node.values?.state;
    if (!vi || 'node' in vi) continue; // skip dynamic (node-ref) inputs

    const cur = (vi as { value?: unknown[] }).value?.[0];
    if (cur !== from) continue;

    (vi as { value: unknown[] }).value = [to];
    changed = true;
  }
  return changed;
}

/**
 * Predicate over a `trace/setState`/`trace/getState` node's inline target path.
 * Receives the RAW path string, or `undefined` when the node has no inline
 * `configuration.target` (a missing target = "self", the graph's host object).
 * `$`-keyword and relative/absolute paths are all passed through as-is — the
 * caller resolves them against the holder to decide scope membership.
 */
export type StateRefTargetPredicate = (targetPath: string | undefined) => boolean;

/** Read a node's inline `configuration.target` path (undefined if absent/dynamic). */
function nodeTargetPath(node: GraphNode): string | undefined {
  const ti = node.configuration?.target as { value?: unknown[] } | undefined;
  const p = ti?.value?.[0];
  return typeof p === 'string' ? p : undefined;
}

/**
 * Cheap read-only check: does `graph` hold ANY inline state-name constant equal to
 * `name` (a `trace/setState` state value or a `math/eq` operand)? Lets a caller skip
 * the deep clone + rewrite when a graph plainly can't reference the renamed/deleted
 * state — the common case on any given rename. Ignores targets (target-scoping is
 * the rewrite's job); a false positive just means the rewrite runs and no-ops.
 */
export function graphReferencesStateName(
  graph: GraphData | null | undefined,
  name: string,
): boolean {
  if (!graph || !Array.isArray(graph.nodes)) return false;
  const isName = (vi: ValueInput | undefined): boolean =>
    !!vi && !('node' in vi) && (vi as { value?: unknown[] }).value?.[0] === name;
  for (const node of graph.nodes) {
    const op = nodeOp(graph, node);
    if (op === 'trace/setState' && isName(node.values?.state)) return true;
    if (op === 'math/eq' && (isName(node.values?.a) || isName(node.values?.b))) return true;
  }
  return false;
}

/**
 * Like {@link rewriteSetStateName}, but only rewrites `trace/setState` nodes whose
 * inline `configuration.target` path satisfies `targetInScope`. IN PLACE; returns
 * true if anything changed.
 *
 * Needed for CROSS-OBJECT propagation: a state rename/delete on one object must also
 * heal `change-state` interactions authored on a DIFFERENT object that target the
 * renamed/deleted state. A blanket name rewrite is unsafe — state names ("State 1")
 * are duplicated across objects — so the caller resolves each node's target and
 * scopes the rewrite to nodes pointing at the affected owner/subtree.
 *
 * `targetInScope` receives the raw target path (or `undefined` for a missing =
 * self target); the caller resolves `$`-keyword/relative/absolute paths.
 */
export function rewriteSetStateNameForTargets(
  graph: GraphData | null | undefined,
  from: string,
  to: string,
  targetInScope: StateRefTargetPredicate,
): boolean {
  if (!graph || !Array.isArray(graph.nodes)) return false;

  let changed = false;
  for (const node of graph.nodes) {
    if (nodeOp(graph, node) !== 'trace/setState') continue;

    const vi = node.values?.state;
    if (!vi || 'node' in vi) continue; // skip dynamic (node-ref) inputs
    const cur = (vi as { value?: unknown[] }).value?.[0];
    if (cur !== from) continue;

    if (!targetInScope(nodeTargetPath(node))) continue;

    (vi as { value: unknown[] }).value = [to];
    changed = true;
  }
  return changed;
}

/**
 * Heal state-name CONSTANTS compared against a `trace/getState` output in cycle
 * graphs. The cycle interaction (`interactionsToBehaviorGraph.addCycleStateFanout`)
 * emits `math/eq` nodes wiring `getState(target).state` against an inline state-name
 * constant, then branches into `setState`. `rewriteSetStateName*` only touches the
 * setState side, so a rename leaves the eq constant stale → the cycle skips that
 * state. This rewrites the inline eq operand IN PLACE. Returns true if anything
 * changed.
 *
 * For each `math/eq` node, symmetric over `values.a`/`values.b`: rewrite the inline
 * operand `{value:[from]}` IFF the OTHER operand is a node-ref `{node, socket:'state'}`
 * pointing at a `trace/getState` node whose target passes `targetInScope`. Both-inline,
 * both-ref, and ref-to-non-getState eq nodes are left untouched (they aren't
 * state-name comparisons we can safely attribute to this target).
 *
 * DELETE (`to === ''`) is a NO-OP here: rewriting the constant to `''` would make the
 * eq match Base (`TraceGetStateBlock` emits `''` for Base) and fire spurious
 * setStates. The Cycle and Toggle shapes a delete does break are healed by
 * {@link healStateRingsOnDelete}. Returns false immediately.
 */
export function rewriteGetStateEqConstants(
  graph: GraphData | null | undefined,
  from: string,
  to: string,
  targetInScope: StateRefTargetPredicate,
): boolean {
  if (to === '') return false; // delete: healStateRingsOnDelete handles the ring shapes (see doc)
  if (!graph || !Array.isArray(graph.nodes)) return false;

  // A node-ref operand points at a `trace/getState` node whose target is in scope.
  const refIsScopedGetState = (vi: ValueInput | undefined): boolean => {
    if (!vi || !('node' in vi)) return false;
    if (vi.socket !== 'state') return false;
    const src = graph.nodes[vi.node];
    if (!src || nodeOp(graph, src) !== 'trace/getState') return false;
    return targetInScope(nodeTargetPath(src));
  };
  // An inline operand holding the `from` state name.
  const inlineIsFrom = (vi: ValueInput | undefined): boolean =>
    !!vi && !('node' in vi) && (vi as { value?: unknown[] }).value?.[0] === from;

  let changed = false;
  for (const node of graph.nodes) {
    if (nodeOp(graph, node) !== 'math/eq') continue;
    const a = node.values?.a;
    const b = node.values?.b;
    // Rewrite the inline operand only when the OTHER operand is a scoped getState.
    if (inlineIsFrom(a) && refIsScopedGetState(b)) {
      (a as { value: unknown[] }).value = [to];
      changed = true;
    } else if (inlineIsFrom(b) && refIsScopedGetState(a)) {
      (b as { value: unknown[] }).value = [to];
      changed = true;
    }
  }
  return changed;
}

/** One step of a compiled state ring: `branch(eq(getState(T), from))` → true → `setState(T, to)`. */
interface StateStep {
  branch: number;
  from: string;
  to: string;
  /** The true-path `trace/setState` node. */
  set: number;
  /** The inline compare operand holding `from`. */
  compare: { value: unknown[] };
  /** The raw target path both nodes address (`undefined` = self). */
  target: string | undefined;
}

function inlineString(vi: ValueInput | undefined): { value: unknown[] } | null {
  if (!vi || 'node' in vi) return null;
  const inline = vi as { value?: unknown[] };
  return typeof inline.value?.[0] === 'string' ? (inline as { value: unknown[] }) : null;
}

function readStateStep(
  graph: GraphData,
  branch: number,
  targetInScope: StateRefTargetPredicate,
): StateStep | null {
  const node = graph.nodes[branch];
  if (!node || nodeOp(graph, node) !== 'flow/branch') return null;
  const cond = node.values?.condition;
  if (!cond || !('node' in cond)) return null;
  const eq = graph.nodes[cond.node];
  if (!eq || nodeOp(graph, eq) !== 'math/eq') return null;

  const getStateOf = (vi: ValueInput | undefined): GraphNode | null => {
    if (!vi || !('node' in vi) || vi.socket !== 'state') return null;
    const src = graph.nodes[vi.node];
    return src && nodeOp(graph, src) === 'trace/getState' ? src : null;
  };
  const refA = getStateOf(eq.values?.a);
  const getState = refA ?? getStateOf(eq.values?.b);
  const compare = inlineString(refA ? eq.values?.b : eq.values?.a);
  if (!getState || !compare) return null;
  const target = nodeTargetPath(getState);
  if (!targetInScope(target)) return null;

  const setIdx = node.flows?.true?.node;
  const set = setIdx === undefined ? undefined : graph.nodes[setIdx];
  if (setIdx === undefined || !set || nodeOp(graph, set) !== 'trace/setState') return null;
  if (nodeTargetPath(set) !== target) return null;
  const to = inlineString(set.values?.state);
  if (!to) return null;

  return { branch, from: compare.value[0] as string, to: to.value[0] as string, set: setIdx, compare, target };
}

/**
 * Point every flow into `branch` at its false successor (or nowhere), then drop
 * the branch's own flows: the branch is left unreachable and inert, so a later
 * heal never reads it as a step. Returns true if anything changed.
 */
function unlinkBranch(graph: GraphData, branch: number): boolean {
  const node = graph.nodes[branch];
  const next = node?.flows?.false;
  let changed = false;
  const repoint = (flows: Record<string, FlowTarget> | undefined): void => {
    if (!flows) return;
    for (const [socket, target] of Object.entries(flows)) {
      if (target.node !== branch) continue;
      if (next) flows[socket] = { ...next };
      else delete flows[socket];
      changed = true;
    }
  };
  for (const n of graph.nodes) repoint(n.flows);
  repoint(graph.inputFlowSockets);
  if (node?.flows) {
    delete node.flows;
    changed = true;
  }
  return changed;
}

function setInlineState(node: GraphNode, name: string): boolean {
  const inline = inlineString(node.values?.state);
  if (!inline || inline.value[0] === name) return false;
  inline.value = [name];
  return true;
}

/**
 * Heal the state rings a DELETE breaks, IN PLACE. Studio compiles a Cycle as a
 * cascade of steps `eq(getState(T), from)` → `setState(T, to)`, each step's false
 * flow leading to the next, and a Toggle as one step whose false flow is
 * `setState(T, from)`. Redirecting only the setState side to Base breaks both: a
 * Cycle never reaches the states after the deleted one, and a Toggle whose
 * compared end was deleted never leaves Base.
 *
 * - Cycle (two or more chained steps): the step into the deleted state goes to
 *   that state's successor, or leaves the cascade when the deleted state was the
 *   stop end; the deleted state's own step leaves the cascade. The result is the
 *   cascade Studio compiles for the remaining states.
 * - Toggle: a deleted compared end becomes Base on both sides, so the toggle runs
 *   Base ↔ the other end.
 *
 * A single step with no false flow is left to the plain redirect: it is also the
 * shape of a one-way "when in A, go to B" guard. Returns true if anything changed.
 */
export function healStateRingsOnDelete(
  graph: GraphData | null | undefined,
  deleted: string,
  targetInScope: StateRefTargetPredicate,
): boolean {
  if (!graph || !Array.isArray(graph.nodes)) return false;

  const steps = new Map<number, StateStep>();
  graph.nodes.forEach((_, i) => {
    const step = readStateStep(graph, i, targetInScope);
    if (step) steps.set(i, step);
  });
  const nextStep = (step: StateStep): StateStep | undefined => {
    const n = graph.nodes[step.branch].flows?.false?.node;
    const next = n === undefined ? undefined : steps.get(n);
    return next && next.target === step.target ? next : undefined;
  };
  const isChained = new Set<number>();
  for (const step of steps.values()) {
    const next = nextStep(step);
    if (next) {
      isChained.add(step.branch);
      isChained.add(next.branch);
    }
  }

  let changed = false;
  for (const step of steps.values()) {
    if (isChained.has(step.branch) || step.from !== deleted) continue;
    const falseIdx = graph.nodes[step.branch].flows?.false?.node;
    const other = falseIdx === undefined ? undefined : graph.nodes[falseIdx];
    if (!other || nodeOp(graph, other) !== 'trace/setState' || nodeTargetPath(other) !== step.target) continue;
    if (inlineString(other.values?.state)?.value[0] !== deleted) continue;
    step.compare.value = [''];
    setInlineState(other, '');
    changed = true;
  }

  // Each cascade from its head (a chained step no other step falls through to).
  const fallenInto = new Set<number>();
  for (const step of steps.values()) {
    const next = nextStep(step);
    if (next) fallenInto.add(next.branch);
  }
  for (const head of steps.values()) {
    if (!isChained.has(head.branch) || fallenInto.has(head.branch)) continue;
    const cascade: StateStep[] = [];
    for (let s: StateStep | undefined = head; s && !cascade.includes(s); s = nextStep(s)) cascade.push(s);

    const own = cascade.find((s) => s.from === deleted);
    for (const into of cascade.filter((s) => s.to === deleted && s !== own)) {
      const healed = own ? setInlineState(graph.nodes[into.set], own.to) : unlinkBranch(graph, into.branch);
      changed = healed || changed;
    }
    if (own) changed = unlinkBranch(graph, own.branch) || changed;
  }
  return changed;
}

/**
 * Run every target-scoped state-ref rewrite over `graph` IN PLACE: on delete the
 * ring heal ({@link healStateRingsOnDelete}) first, then setState inline names
 * ({@link rewriteSetStateNameForTargets}) + getState/eq cycle constants
 * ({@link rewriteGetStateEqConstants}). No short-circuit — every pass runs.
 * Returns true if any changed anything.
 */
export function rewriteStateRefsForTargets(
  graph: GraphData | null | undefined,
  from: string,
  to: string,
  targetInScope: StateRefTargetPredicate,
): boolean {
  const ringsChanged = to === '' ? healStateRingsOnDelete(graph, from, targetInScope) : false;
  const setStateChanged = rewriteSetStateNameForTargets(graph, from, to, targetInScope);
  const eqChanged = rewriteGetStateEqConstants(graph, from, to, targetInScope);
  return ringsChanged || setStateChanged || eqChanged;
}
